import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, useState, type ReactElement} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import type {Location} from "@khan/urls";

import type {ClassLink, MyClassesSection} from "../homepage-data.ts";

import Section from "./section.tsx";

const MAX_CLASSES = 10;

const displayableClasses = (
    showAllClasses: boolean,
    classLinks: ClassLink[],
    location: Location,
): MyClassesSection["links"] => {
    if (showAllClasses) {
        return classLinks;
    }

    const currentClassIndex = classLinks.findIndex((link) =>
        link.linkMatcher(null, location),
    );

    // Push current class to be the last element before cutoff
    if (currentClassIndex >= MAX_CLASSES) {
        return [
            ...classLinks.slice(0, MAX_CLASSES - 1),
            classLinks[currentClassIndex],
        ];
    }

    return classLinks.slice(0, MAX_CLASSES);
};

const ClassSections = ({
    classSection,
    location,
}: {
    classSection: MyClassesSection;
    location: Location;
}): ReactElement => {
    const [showAllClasses, setShowAllClasses] = useState(false);

    const allClassesCount = classSection.links.length;
    const linksWithCutoff = displayableClasses(
        showAllClasses,
        classSection.links,
        location,
    );

    return (
        <div data-testid="class-nav-links">
            <Section section={{...classSection, links: linksWithCutoff}} />
            {allClassesCount > MAX_CLASSES && !showAllClasses && (
                <Button
                    kind="tertiary"
                    onClick={() => setShowAllClasses(true)}
                    style={styles.moreButton}
                >
                    {t`See all ${allClassesCount} classes`}
                </Button>
            )}
        </div>
    );
};

const styles = StyleSheet.create({
    moreButton: {
        marginTop: 0,
        marginBottom: 22,
        height: 16,
        marginLeft: 24,
    },
});

export default memo(ClassSections);
